import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql, Card, CardContent } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh, SaveButton } from '@ah/design/mobile';
import {
  ArrowRight, BookOpen, Check, Clock,
} from 'lucide-react';
import {
  BOOK_DETAIL_QUERY,
  RECORD_BOOK_ENGAGEMENT_MUTATION,
  RECORD_BOOK_SAVE_MUTATION,
  RECORD_BOOK_UNSAVE_MUTATION,
  type BookDetail as BookDetailData,
  useBookProgress,
} from '../shared/index.ts';

export function BookDetail({ inSheet }: { inSheet?: boolean } = {}) {
  const gql = useGql();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { olid = '' } = useParams();
  const olKey = `/works/${olid}`;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['books', 'detail', olKey],
    queryFn: async () => {
      const res = await gql.request<{ book: BookDetailData | null }>(BOOK_DETAIL_QUERY, { olKey });
      return res.book;
    },
    enabled: !!olid,
  });
  const progress = useBookProgress(olid);
  const bookId = data?.id;
  useEffect(() => {
    if (bookId) gql.request(RECORD_BOOK_ENGAGEMENT_MUTATION, { id: bookId }).catch(() => {});
  }, [bookId, gql]);

  const body = isLoading ? (
    <DetailSkeleton hero="book" />
  ) : error || !data ? (
    <p className="px-4 py-10 text-center text-sm text-destructive">Book not found.</p>
  ) : (
    <Body book={data} olid={olid} progress={progress.chaptersRead} />
  );

  if (inSheet) return <>{body}</>;

  const trailing = data && bookId ? (
    <SaveButton
      isSaved={data.isSaved}
      onSave={async () => {
        await gql.request(RECORD_BOOK_SAVE_MUTATION, { id: bookId });
        qc.invalidateQueries({ queryKey: ['books', 'saved'] });
        qc.setQueryData(['books', 'detail', olKey], (prev: BookDetailData | null | undefined) =>
          prev ? { ...prev, isSaved: true } : prev,
        );
      }}
      onUnsave={async () => {
        await gql.request(RECORD_BOOK_UNSAVE_MUTATION, { id: bookId });
        qc.invalidateQueries({ queryKey: ['books', 'saved'] });
        qc.setQueryData(['books', 'detail', olKey], (prev: BookDetailData | null | undefined) =>
          prev ? { ...prev, isSaved: false } : prev,
        );
      }}
    />
  ) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.title ?? 'Book'} backTo="/library/books" trailing={trailing} />
      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {body}
      </PullToRefresh>
    </div>
  );
}

function Body({
  book,
  olid,
  progress,
}: {
  book: BookDetailData;
  olid: string;
  progress: number[];
}) {
  const { summary } = book;
  const chapters = summary?.chapters ?? [];
  const readSet = new Set(progress);
  const firstUnread = chapters.findIndex((_, i) => !readSet.has(i + 1));
  const resumeChapter = firstUnread === -1 ? 1 : firstUnread + 1;
  const readCount = readSet.size;
  const totalCount = chapters.length;
  const pct = totalCount ? Math.round((readCount / totalCount) * 100) : 0;
  const ctaLabel = readCount === 0
    ? 'Start reading'
    : readCount === totalCount
      ? 'Re-read from chapter 1'
      : `Continue chapter ${resumeChapter}`;
  const ctaTarget = readCount === totalCount ? 1 : resumeChapter;

  return (
    <article className="px-4 pb-10 space-y-6">
      <div className="-mx-4 px-4 py-6 bg-muted/40 flex flex-col items-center">
        <div className="w-32 aspect-[2/3] rounded-lg overflow-hidden bg-muted shadow-lg">
          {book.coverUrl ? (
            <img src={book.coverUrl} alt={book.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-muted-foreground">
              <BookOpen size={32} className="opacity-40" />
            </div>
          )}
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold leading-tight">{book.title}</h1>
        {book.subtitle ? (
          <p className="text-sm text-muted-foreground mt-1">{book.subtitle}</p>
        ) : null}
        <p className="text-sm mt-2">
          by {book.authors.map((a) => a.name).join(', ')}
          {book.firstPublishYear ? (
            <span className="text-muted-foreground"> · {book.firstPublishYear}</span>
          ) : null}
        </p>
        {summary?.readingMinutesEst ? (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-2">
            <Clock size={12} /> {summary.readingMinutesEst} min · {totalCount} chapters
          </span>
        ) : null}
        {summary?.themes?.length ? (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {summary.themes.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {chapters.length ? (
        <div>
          <Link
            to={`/library/books/${olid}/read/${ctaTarget}`}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
          >
            {ctaLabel} <ArrowRight size={16} />
          </Link>
          {readCount > 0 && readCount < totalCount ? (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {readCount} of {totalCount} chapters read · {pct}%
            </p>
          ) : null}
          {readCount === totalCount ? (
            <p className="text-xs text-muted-foreground mt-2 text-center inline-flex items-center gap-1 justify-center w-full">
              <Check size={12} /> Finished
            </p>
          ) : null}
        </div>
      ) : null}

      {summary?.status === 'generated' && summary.hook ? (
        <p className="text-base leading-relaxed italic text-muted-foreground border-l-2 border-border pl-3">
          {summary.hook}
        </p>
      ) : null}

      {summary?.youllLikeThisIf?.length ? (
        <section>
          <h2 className="text-lg font-semibold mb-3">You'll like this if</h2>
          <ul className="space-y-1.5">
            {summary.youllLikeThisIf.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="text-muted-foreground">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {chapters.length ? (
        <section>
          <h2 className="text-lg font-semibold mb-3">What's inside</h2>
          <ol className="space-y-1">
            {chapters.map((c, i) => {
              const n = i + 1;
              const read = readSet.has(n);
              return (
                <li key={i}>
                  <Link
                    to={`/library/books/${olid}/read/${n}`}
                    className="flex items-baseline gap-3 py-2.5 px-2 rounded-md active:bg-accent"
                  >
                    <span
                      className={
                        'flex-shrink-0 w-6 h-6 rounded-full grid place-items-center text-[11px] tabular-nums ' +
                        (read
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-muted-foreground/30 text-muted-foreground')
                      }
                    >
                      {read ? <Check size={12} /> : n}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">{c.title}</div>
                      {c.keyTakeaway ? (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {c.keyTakeaway}
                        </div>
                      ) : null}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      {summary?.status === 'ineligible' ? (
        <Card className="bg-muted/30 border-none">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">
              We don't have a summary for this book yet — our model declined to write one without
              first-hand knowledge of the text.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {!summary ? (
        <Card className="bg-muted/30 border-none">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">
              Summary not generated yet for this book.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {book.description ? (
        <section>
          <h2 className="text-lg font-semibold mb-3">Publisher description</h2>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{book.description}</p>
        </section>
      ) : null}
    </article>
  );
}
